import { AcademicEnrollmentType } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  ProfileFields,
  UserProfileResponse,
  ProfileValueOptionResponse,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { ChecklistDataOverrides } from '../assessMyTrack.types';

const ASSESSMENT_CHECKLIST_STRINGS = appStrings.modules.assessMyTrack.assessmentChecklist;

export type ChecklistItem = Readonly<{
  label: string;
  isComplete: boolean;
  description?: string;
  fieldKey: ProfileFields | ProfileFields[] | null;
  enrollmentType?: AcademicEnrollmentType | null;
  customOnEdit?: (navigate: (path: string) => void, doesUserHaveBlueprint: boolean) => void;
  optional?: boolean;
}>;

type ChecklistOverrideExtras = Pick<
  ChecklistDataOverrides,
  'profile_image_url' | 'career_user_response_set_id'
>;

export type ChecklistProfileData = Readonly<{
  profile: Partial<UserProfileResponse['profile']> & Partial<ChecklistOverrideExtras>;
}>;

export type ChecklistItemConfig = Omit<ChecklistItem, 'isComplete' | 'description'> & {
  getIsComplete: ({
    profileData,
    isBlueprintComplete,
    isLifeReadinessComplete,
  }: {
    profileData?: ChecklistProfileData;
    isBlueprintComplete?: boolean;
    isLifeReadinessComplete?: boolean;
  }) => boolean;
  getDescription: ({
    profileData,
    profileOptions,
    doesUserHaveBlueprint,
    isLifeReadinessComplete,
  }: {
    profileData?: ChecklistProfileData;
    profileOptions?: ProfileValueOptionResponse;
    doesUserHaveBlueprint?: boolean;
    isLifeReadinessComplete?: boolean;
  }) => string;
  editModal:
    | {
        title: string;
        description?: string;
        doneButtonText?: string;
      }
    | undefined;
  optional?: boolean;
};

export const assessmentChecklistConfig: ChecklistItemConfig[] = [
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.gpa.title,
    fieldKey: 'high_school_gpa',
    enrollmentType: 'high_school',
    getIsComplete: ({ profileData }) => {
      const metrics = profileData?.profile.academicMetrics;

      return !!metrics?.high_school_gpa;
    },
    getDescription: ({ profileData }) => {
      const metrics = profileData?.profile.academicMetrics;

      return (
        metrics?.high_school_gpa?.toString() ||
        ASSESSMENT_CHECKLIST_STRINGS.checklistItems.gpa.description
      );
    },
    editModal: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.gpa.editModal,
  },
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.gpa.title,
    fieldKey: 'post_secondary_gpa',
    enrollmentType: 'post_secondary',
    getIsComplete: ({ profileData }) => {
      const metrics = profileData?.profile.academicMetrics;

      return !!metrics?.post_secondary_gpa;
    },
    getDescription: ({ profileData }) => {
      const metrics = profileData?.profile.academicMetrics;

      return (
        metrics?.post_secondary_gpa?.toString() ||
        ASSESSMENT_CHECKLIST_STRINGS.checklistItems.gpa.description
      );
    },
    editModal: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.gpa.editModal,
  },
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.postSecondaryEnrollment.title,
    fieldKey: 'academicsPostSecondaryCurrentEnrollment',
    enrollmentType: 'post_secondary',
    getIsComplete: ({ profileData }) => {
      return !!profileData?.profile.academicsPostSecondaryCurrentEnrollment;
    },
    getDescription: ({ profileData }) => {
      if (typeof profileData?.profile.academicsPostSecondaryCurrentEnrollment === 'string') {
        return profileData?.profile.academicsPostSecondaryCurrentEnrollment;
      }

      return (
        profileData?.profile.academicsPostSecondaryCurrentEnrollment?.school ||
        ASSESSMENT_CHECKLIST_STRINGS.checklistItems.postSecondaryEnrollment.description
      );
    },
    editModal: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.postSecondaryEnrollment.editModal,
  },
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.graduationYear.title,
    fieldKey: 'academicPostSecondaryExpectedGraduationYear',
    enrollmentType: 'post_secondary',
    getIsComplete: ({ profileData }) => {
      return !!profileData?.profile.academicPostSecondaryExpectedGraduationYear;
    },
    getDescription: ({ profileData }) => {
      const year = profileData?.profile.academicPostSecondaryExpectedGraduationYear;

      return (
        year?.toString() || ASSESSMENT_CHECKLIST_STRINGS.checklistItems.graduationYear.description
      );
    },
    editModal: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.graduationYear.editModal,
  },
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.coursework.title,
    fieldKey: 'academicRigor',
    enrollmentType: 'high_school',
    getIsComplete: ({ profileData }) => {
      return !!profileData?.profile.academicRigor;
    },
    getDescription: ({ profileData, profileOptions }) => {
      const academicRigorId = profileData?.profile.academicRigor;

      if (academicRigorId && profileOptions?.academicRigor) {
        const rigorOption = profileOptions.academicRigor.find(
          (option) => option.id === academicRigorId,
        );

        if (rigorOption) {
          return rigorOption.name;
        }
      }

      return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.coursework.description;
    },
    editModal: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.coursework.editModal,
  },
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.graduationYear.title,
    fieldKey: 'academicHighSchoolExpectedGraduationYear',
    enrollmentType: 'high_school',
    getIsComplete: ({ profileData }) => {
      return !!profileData?.profile.academicHighSchoolExpectedGraduationYear;
    },
    getDescription: ({ profileData }) => {
      const year = profileData?.profile.academicHighSchoolExpectedGraduationYear;

      return (
        year?.toString() || ASSESSMENT_CHECKLIST_STRINGS.checklistItems.graduationYear.description
      );
    },
    editModal: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.graduationYear.editModal,
  },
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.major.title,
    fieldKey: 'academicMajors',
    enrollmentType: 'post_secondary',
    getIsComplete: ({ profileData }) => {
      const major = profileData?.profile.academicMajors
        ?.map((major) => major.academic_majors_id)
        .join(', ');

      return !!major;
    },
    getDescription: ({ profileData, profileOptions }) => {
      const academicMajors = profileData?.profile.academicMajors;

      if (academicMajors && profileOptions?.academicMajors) {
        const majorNames = academicMajors
          .map((major) => {
            const majorOption = profileOptions.academicMajors.find(
              (option) => option.id === major.academic_majors_id,
            );

            return majorOption ? majorOption.name : '';
          })
          .join(', ');

        if (majorNames) {
          return majorNames;
        }
      }

      return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.major.description;
    },
    editModal: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.major.editModal,
  },
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.resume.title,
    fieldKey: 'documentsResumes',
    enrollmentType: null,
    getIsComplete: ({ profileData }) => {
      const documentsResumes = profileData?.profile.documentsResumes;

      return !!documentsResumes && documentsResumes.length > 0;
    },
    getDescription: ({ profileData }) => {
      const documents = profileData?.profile.documentsResumes;

      if (documents && documents.length > 0) {
        return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.resume.descriptionComplete;
      }

      return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.resume.description;
    },
    editModal: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.resume.editModal,
  },
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.blueprint.title,
    fieldKey: null,
    enrollmentType: null,
    optional: false,
    getIsComplete: ({ isBlueprintComplete }) => {
      return !!isBlueprintComplete;
    },
    customOnEdit: (navigate, doesUserHaveBlueprint) => {
      navigate(doesUserHaveBlueprint ? APP_ROUTE.MY_BLUEPRINT : APP_ROUTE.SURVEY);
    },
    getDescription: ({ doesUserHaveBlueprint }) => {
      if (doesUserHaveBlueprint) {
        return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.blueprint.descriptionComplete;
      }

      return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.blueprint.description;
    },
    editModal: undefined,
  },
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.academicMetrics.title,
    fieldKey: ['sat_score', 'act_score'],
    enrollmentType: null,
    optional: true,
    getIsComplete: ({ profileData }) => {
      const actScore = profileData?.profile.academicMetrics?.act_score;

      return !!actScore;
    },
    getDescription: ({ profileData }) => {
      const actScore = profileData?.profile.academicMetrics?.act_score;
      const satScore = profileData?.profile.academicMetrics?.sat_score;

      if (satScore || actScore) {
        return [
          satScore && `SAT: ${Math.trunc(satScore)}`,
          actScore && `ACT: ${Math.trunc(actScore)}`,
        ]
          .filter(Boolean)
          .join(', ');
      }

      return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.academicMetrics.description;
    },
    editModal: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.academicMetrics.editModal,
  },
  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.lifeReadiness.title,
    fieldKey: null,
    enrollmentType: null,
    optional: true,
    getIsComplete: ({ isLifeReadinessComplete }) => {
      return !!isLifeReadinessComplete;
    },
    customOnEdit: (navigate) => {
      navigate(APP_ROUTE.LIFE_READINESS);
    },
    getDescription: ({ isLifeReadinessComplete }) => {
      if (isLifeReadinessComplete) {
        return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.lifeReadiness.descriptionComplete;
      }

      return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.lifeReadiness.description;
    },
    editModal: undefined,
  },

  {
    label: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.transcript.title,
    fieldKey: 'documentsTranscripts',
    enrollmentType: null,
    getIsComplete: ({ profileData }) => {
      const documentsTranscripts = profileData?.profile.documentsTranscripts;

      return !!documentsTranscripts && documentsTranscripts.length > 0;
    },
    getDescription: ({ profileData }) => {
      const documents = profileData?.profile.documentsTranscripts;

      if (documents && documents.length > 0) {
        return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.transcript.descriptionComplete;
      }

      return ASSESSMENT_CHECKLIST_STRINGS.checklistItems.transcript.description;
    },
    editModal: ASSESSMENT_CHECKLIST_STRINGS.checklistItems.transcript.editModal,
  },
];
